import React from "react";

/**
 * Where a written support request goes.
 *
 * Hard-coded for the same reason the Cal link is: it is the same inbox for
 * every workspace, and a support address that could differ per environment is a
 * way of quietly sending real people's problems into a staging mailbox.
 */
export const SUPPORT_EMAIL = "hello@naurist.com";

/**
 * Settings → Support: the way to reach us in writing, beside the booker.
 *
 * The tab offers a call and nothing else, and a call is the wrong size for most
 * of what people want to say — a question with a screenshot attached, a thing
 * that went wrong at four in the morning, anything that does not need half an
 * hour of somebody's diary. This is that other size.
 *
 * An address and not a form. There is no endpoint that takes a written request;
 * the Support tab used to draw a form for one, and it was taken out precisely
 * because it reached nobody. Printing the address is the honest version of the
 * same offer, and it also means their sent copy, their attachments and their
 * thread all live in the mailbox they already read, rather than in a form they
 * pressed Submit on and can never see again.
 *
 * Text and not a link, deliberately. Opening a draft for somebody is the sort
 * of help that only helps when it works, and it does not reliably: a `mailto:`
 * goes to whatever mail client the machine has registered, and a browser with
 * none — which is most of them — drops it in silence, no error and no tab. The
 * card would look pressable and do nothing. An address on the screen can be
 * read, selected, copied and typed into whatever they actually write mail in,
 * on any machine, and it never fails quietly. So there is no `href`, no hover
 * state and nothing to click: what looks inert is inert.
 */
const ContactUsCard: React.FC = () => (
  /* `h-full` so this stands the height of the form beside it rather than
     stopping at its own copy — the wrapper in `SupportTab` is what this fills,
     and what holds it level with the top of that form.

     White like every other card on the panel, but ruled in the brand's pale
     teal where the rest take `line`: this is the one thing on the tab that is
     not part of the booking flow down the left, and it has to read as a second
     way out rather than as another step. The tint that does the marking is on
     the address panel below, which is the part worth taking away. */
  <div className="flex h-full flex-col card-surface border border-[#A6EDF5] bg-white p-5 sm:p-6 3xl:p-7">
    <h2 className="text-[18px] 3xl:text-[20px] font-semibold text-[#00424B]">
      Reach us via email
    </h2>

    {/* Tight under the heading and a size down from it, so heading and line
        read as one block of copy rather than as two things stacked. */}
    <p className="mt-1.5 text-[14px] 3xl:text-[15px] leading-relaxed text-ink">
      Prefer email over a call? Drop us a message and our team will get back to
      you.
    </p>

    {/* `mt-auto` pins the address to the foot of the card however tall the
        column beside it grows. The copy above it is a heading and a line; the
        space between is the card matching the form's height, not a gap anybody
        measured.

        A tinted panel with no border and no hover: it is a plaque, not a
        button. The fill is enough to lift the address off the card and mark it
        as the thing to take away, and anything more — an outline, a colour that
        moves under the pointer — would promise a click there is nothing behind.

        `select-all` so one click takes the whole address rather than the word
        under the pointer, which is the only gesture this card owes anybody. */}
    <div className="mt-auto select-all rounded-md bg-brand-soft px-4 py-3.5 3xl:px-5 3xl:py-4">
      <span className="block text-[12px] 3xl:text-[13px] uppercase tracking-wide text-ink-muted">
        Email:
      </span>
      {/* `break-all` rather than a truncation: an address is only useful whole,
          and this is the one on the card rather than one of many in a list, so
          there is room to wrap it. */}
      <span className="mt-0.5 block break-all text-[16px] sm:text-[17px] 3xl:text-[18px] font-semibold text-[#00424B]">
        {SUPPORT_EMAIL}
      </span>
    </div>

    {/* Under the panel rather than inside it: it is what happens after the
        message is sent, not part of the address. Said at all because the
        alternative on this tab is a call with a time on it, and an address with
        nothing next to it looks like the slower of the two by an unknown
        amount. */}
    <p className="mt-2.5 text-[12px] 3xl:text-[13px] text-ink-muted">
      Typical reply within 12-24 hours
    </p>
  </div>
);

export default ContactUsCard;
